import React, { useEffect, useState } from "react";
import {
  Typography,
  Button,
  RadioGroup,
  Radio,
  FormControlLabel,
} from "@mui/material";
// import CloseIcon from "@mui/icons-material/Close";
import { Formik, Form, Field, useFormikContext } from "formik";
import * as Yup from "yup";
import styles from "./walkTestSecondStep.module.css";
import DateTimeField from "./DateTimeField";
import WalkTestChart from "./WalkTestChart";
import axios from "../../utils/lib/axios";
import { useDispatch, useSelector } from "react-redux";
import {
  setWalkTestFormData,
  clearWalkTestFormData,
} from "../../store/actions/walkTestAction";

const isPositive = (message = "must be a positive number") =>
  Yup.string().test("positive", message, (value) => {
    const num = parseFloat(value);
    return !value || num >= 0;
  });

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .max(60, "Name must be at most 60 characters")
    .required("Name is required"),
  date: Yup.string().required("Date is required"),
  patientId: Yup.string().max(15, "Patient ID must be at most 15 characters"),
  units: Yup.string().required("Units selection is required"),

  height_ft: Yup.string().when("units", {
    is: "customary",
    then: Yup.string().required("Feet is required").concat(isPositive()),
    otherwise: Yup.string().notRequired(),
  }),

  height_in: Yup.string().when("units", {
    is: "customary",
    then: Yup.string().required("Inches is required").concat(isPositive()),
    otherwise: Yup.string().notRequired(),
  }),

  height_cm: Yup.string().when("units", {
    is: "metric",
    then: Yup.string()
      .required("Height in cm is required")
      .concat(isPositive()),
    otherwise: Yup.string().notRequired(),
  }),

  weight: Yup.string().required("Weight is required").concat(isPositive()),

  bmi: Yup.string(),
  birthday: Yup.date()
    .required("Birth date is required")
    .max(new Date(), "Birth date cannot be in the future"),
  age: Yup.string(),
  birthSex: Yup.string().required("Birth sex is required"),
  gender: Yup.string().max(10, "Gender must be at most 10 characters"),
  icd10: Yup.string(),
  smokingHistory: Yup.number()
    .typeError("Must be a number")
    .integer("Must be an integer")
    .min(0, "Must be a positive integer"),
  predictedGroup: Yup.string().max(
    15,
    "Predicted group must be at most 15 characters"
  ),
  technician: Yup.string().required("Team Staff is required"),
  attendingPhysician: Yup.string().required("Attending Team Staff is required"),
  referringPhysician: Yup.string().required("Referring Team Staff is required"),

  actualDistance: Yup.string()
    .required("Actual distance is required")
    .concat(isPositive()),

  predictedDistance: Yup.string(),
  lln: Yup.string(),
  percentPredicted: Yup.string(),
  avgWalkingSpeed: Yup.string(),
  supplementalOxygen: Yup.string().max(
    25,
    "Supplemental Oxygen must be at most 25 characters"
  ),
  walkStatement: Yup.string()
    .required("6-Minute Walk Statement is required")
    .max(1000, "6-Minute Walk Statement must be at most 1000 characters"),
  techComments: Yup.string()
    .required("Team Staff Comments is required")
    .max(1000, "Team Staff Comments must be at most 1000 characters"),
  assignedTo: Yup.string(),
});

const Calculator = () => {
  const { values, setFieldValue } = useFormikContext();

  useEffect(() => {
    // Calculate BMI
    const calculateBMI = () => {
      try {
        if (values.units === "customary") {
          const heightInInches =
            parseFloat(values.height_ft || 0) * 12 +
            parseFloat(values.height_in || 0);
          const weight = parseFloat(values.weight || 0);

          if (heightInInches > 0 && weight > 0) {
            const bmi = (
              (weight * 703) /
              (heightInInches * heightInInches)
            ).toFixed(1);
            return isNaN(bmi) ? "" : bmi;
          }
        } else {
          const heightInMeters = parseFloat(values.height_cm || 0) / 100;
          const weight = parseFloat(values.weight || 0);

          if (heightInMeters > 0 && weight > 0) {
            const bmi = (weight / (heightInMeters * heightInMeters)).toFixed(1);
            return isNaN(bmi) ? "" : bmi;
          }
        }
      } catch (error) {
        console.error("BMI calculation error:", error);
      }
      return "";
    };

    // Set BMI value
    const bmi = calculateBMI();
    setFieldValue("bmi", bmi);

    // Calculate other values
    const predDistance = calculatePredictedDistance(
      {
        ft: values.height_ft,
        in: values.height_in,
        cm: values.height_cm,
      },
      values.weight,
      values.age,
      values.birthSex,
      values.units
    );
    setFieldValue("predictedDistance", predDistance);

    // Calculate LLN with proper units
    const lln = calculateLLN(predDistance, values.birthSex, values.units);
    setFieldValue("lln", lln);

    // Calculate percent predicted
    if (predDistance && values.actualDistance) {
      const percentPred =
        (parseFloat(values.actualDistance) / parseFloat(predDistance)) * 100;
      setFieldValue("percentPredicted", percentPred.toFixed(2));
    }

    // Calculate walking speed
    const walkSpeed = calculateWalkingSpeed(
      values.actualDistance,
      values.units
    );
    setFieldValue("avgWalkingSpeed", walkSpeed);
  }, [
    values.height_ft,
    values.height_in,
    values.height_cm,
    values.weight,
    values.age,
    values.birthSex,
    values.actualDistance,
    values.units,
    setFieldValue,
  ]);

  return null;
};

const calculateAge = (birthDate) => {
  if (!birthDate) return "";
  const today = new Date();
  const birth = new Date(birthDate);
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age.toString();
};

const calculatePredictedDistance = (height, weight, age, gender, units) => {
  try {
    // Convert to metric if needed
    const heightCm =
      units === "customary"
        ? parseFloat(height.ft || 0) * 30.48 + parseFloat(height.in || 0) * 2.54
        : parseFloat(height.cm || 0);

    const weightKg =
      units === "customary"
        ? parseFloat(weight) * 0.453592
        : parseFloat(weight);

    if (!heightCm || !weightKg || !age) return "";

    let predictedMeters = 0;

    // Calculate predicted distance in meters
    if (gender === "male") {
      predictedMeters = 7.57 * heightCm - 5.02 * age - 1.76 * weightKg - 309;
    } else if (gender === "female") {
      predictedMeters = 2.11 * heightCm - 2.29 * weightKg - 5.78 * age + 667;
    }

    // Convert back to feet if using customary units
    return units === "customary"
      ? (predictedMeters * 3.28084).toFixed(2)
      : predictedMeters.toFixed(2);
  } catch (error) {
    console.error("Predicted distance calculation error:", error);
    return "";
  }
};

const calculateLLN = (predictedDistance, gender, units) => {
  if (!predictedDistance) return "";
  try {
    const pred = parseFloat(predictedDistance);
    const lowerLimitMeters = gender === "male" ? pred - 153 : pred - 139;

    // Convert result to feet if using customary units
    return units === "customary"
      ? (lowerLimitMeters * 3.28084).toFixed(2)
      : lowerLimitMeters.toFixed(2);
  } catch (error) {
    console.error("LLN calculation error:", error);
    return "";
  }
};

const calculateWalkingSpeed = (distance, units) => {
  try {
    // Convert to meters if in feet
    const distanceInMeters =
      units === "customary"
        ? parseFloat(distance) * 0.3048
        : parseFloat(distance);

    if (!distanceInMeters) return "";

    // Calculate speed in km/h first
    const speedKmH = (distanceInMeters / 360) * 3.6;

    // Return in mph if using customary units, otherwise in km/h
    return units === "customary"
      ? (speedKmH * 0.621371).toFixed(2)
      : speedKmH.toFixed(2);
  } catch (error) {
    console.error("Walking speed calculation error:", error);
    return "";
  }
};

const CM_PER_INCH = 2.54;
const KG_PER_LB = 0.45359237;
const LBS_PER_KG = 1 / KG_PER_LB;

const parseNumericField = (value) => {
  const num = parseFloat(value);
  return Number.isFinite(num) ? num : null;
};

const formatConvertedNumber = (num, decimals = 1) => {
  const rounded = Number(num.toFixed(decimals));
  return rounded % 1 === 0 ? String(Math.round(rounded)) : String(rounded);
};

const convertCustomaryToMetric = (heightFt, heightIn, weightLbs) => {
  const ft = parseNumericField(heightFt);
  const inches = parseNumericField(heightIn);
  const lbs = parseNumericField(weightLbs);

  let height_cm = "";
  if (ft !== null || inches !== null) {
    const totalInches = (ft || 0) * 12 + (inches || 0);
    if (totalInches > 0) {
      height_cm = formatConvertedNumber(totalInches * CM_PER_INCH, 1);
    }
  }

  let weight = "";
  if (lbs !== null && lbs > 0) {
    weight = formatConvertedNumber(lbs * KG_PER_LB, 1);
  }

  return { height_cm, weight };
};

const convertMetricToCustomary = (heightCm, weightKg) => {
  const cm = parseNumericField(heightCm);
  const kg = parseNumericField(weightKg);

  let height_ft = "";
  let height_in = "";
  if (cm !== null && cm > 0) {
    const totalInches = cm / CM_PER_INCH;
    const feet = Math.floor(totalInches / 12);
    const remainingInches = totalInches - feet * 12;
    height_ft = String(feet);
    height_in = formatConvertedNumber(remainingInches, 1);
  }

  let weight = "";
  if (kg !== null && kg > 0) {
    weight = formatConvertedNumber(kg * LBS_PER_KG, 1);
  }

  return { height_ft, height_in, weight };
};

function WalkTestSecondStep({
  onBack,
  formData,
  onClose,
  admins,
  handleConfirmExit,
  loadCompletedData,
  loadWaitingData,
  setAlert,
  icdTens,
  onWalkTestReportGenerated,
}) {
  const formikRef = React.useRef();
  const dispatch = useDispatch();
  const savedFormData = useSelector((state) => state.walkTest.formData);
  const savedDyspneaValues = useSelector(
    (state) => state.walkTest.dyspneaValues
  );
  const savedFatigueValues = useSelector(
    (state) => state.walkTest.fatigueValues
  );
  const savedBpValues = useSelector((state) => state.walkTest.bpValues);

  const [selectedValues, setSelectedValues] = useState({
    rest: formData?.rest_min?.find((item) => item.is_selected) || null,
    walk_1: formData?.walk_min_1?.find((item) => item.is_selected) || null,
    walk_2: formData?.walk_min_2?.find((item) => item.is_selected) || null,
    walk_3: formData?.walk_min_3?.find((item) => item.is_selected) || null,
    walk_4: formData?.walk_min_4?.find((item) => item.is_selected) || null,
    walk_5: formData?.walk_min_5?.find((item) => item.is_selected) || null,
    walk_6: formData?.walk_min_6?.find((item) => item.is_selected) || null,
    recovery_1:
      formData?.recovery_min_1?.find((item) => item.is_selected) || null,
    recovery_2:
      formData?.recovery_min_2?.find((item) => item.is_selected) || null,
    recovery_3:
      formData?.recovery_min_3?.find((item) => item.is_selected) || null,
  });

  // Initialize with saved values from Redux if they exist, otherwise use defaults
  const [dyspneaValues, setDyspneaValues] = useState(
    savedDyspneaValues || {
      rest: "",
      max: "",
      recovery_1: "",
      recovery_2: "",
      recovery_3: "",
    }
  );

  const [fatigueValues, setFatigueValues] = useState(
    savedFatigueValues || {
      rest: "",
      max: "",
      recovery_1: "",
      recovery_2: "",
      recovery_3: "",
    }
  );

  const [bpValues, setBpValues] = useState(
    savedBpValues || {
      rest: "",
      max: "",
      recovery_1: "",
      recovery_2: "",
      recovery_3: "",
    }
  );

  // Add a validation state for dyspnea, fatigue, PR bpm and SpO2 fields
  const [formValidation, setFormValidation] = useState({
    dyspneaFatigueValid: false,
    prBpmValid: false,
    spO2Valid: false,
  });

  const patientInitialValues = {
    name: "",
    date: new Date().toISOString().split("T")[0],
    patientId: "",
    units: "customary",
    height_ft: "",
    height_in: "",
    height_cm: "",
    weight: "",
    bmi: "",
    birthday: "",
    age: "",
    birthSex: "",
    gender: "",
    icd10: "",
    smokingHistory: "",
    predictedGroup: "",
    technician: "",
    attendingPhysician: "",
    referringPhysician: "",
    actualDistance: "",
    predictedDistance: "",
    lln: "",
    percentPredicted: "",
    avgWalkingSpeed: "",
    supplementalOxygen: "",
    walkStatement: "",
    techComments: "",
    assignedTo: "",
  };

  const handleUnitChange = (setFieldValue, newUnit, currentValues) => {
    if (currentValues.units === newUnit) return;

    if (newUnit === "metric") {
      const { height_cm, weight } = convertCustomaryToMetric(
        currentValues.height_ft,
        currentValues.height_in,
        currentValues.weight,
      );
      setFieldValue("height_ft", "");
      setFieldValue("height_in", "");
      setFieldValue("height_cm", height_cm);
      setFieldValue("weight", weight);
    } else {
      const { height_ft, height_in, weight } = convertMetricToCustomary(
        currentValues.height_cm,
        currentValues.weight,
      );
      setFieldValue("height_cm", "");
      setFieldValue("height_ft", height_ft);
      setFieldValue("height_in", height_in);
      setFieldValue("weight", weight);
    }

    // Actual distance is stored in meters (metric) or feet (customary) —
    // convert it along with height/weight so it doesn't stay displayed in
    // the old unit after switching. (predictedDistance is a disabled field
    // reactively recalculated from height/weight/age/units elsewhere.)
    const distanceFactor = newUnit === "customary" ? 3.28084 : 0.3048;
    const actualDistance = parseNumericField(currentValues.actualDistance);
    setFieldValue(
      "actualDistance",
      actualDistance !== null
        ? formatConvertedNumber(actualDistance * distanceFactor, 1)
        : "",
    );

    setFieldValue("units", newUnit);
  };

  // Update validation when values change
  useEffect(() => {
    // Validate dyspnea and fatigue
    const isDyspneaFatigueValid =
      dyspneaValues.rest &&
      dyspneaValues.max &&
      fatigueValues.rest &&
      fatigueValues.max;

    // Validate PR bpm and SpO2 fields
    let isPrBpmValid = true;
    let isSpO2Valid = true;

    // Check all required fields in selectedValues
    const requiredKeys = [
      "rest",
      "walk_1",
      "walk_2",
      "walk_3",
      "walk_4",
      "walk_5",
      "walk_6",
      "recovery_1",
      "recovery_2",
      "recovery_3",
    ];

    // Validate if all selected values have PR and SpO2
    for (const key of requiredKeys) {
      if (selectedValues[key]) {
        // Check if PR value is empty or N/A
        const prValue = selectedValues[key].pr;
        if (!prValue || prValue === "N/A" || prValue === "-") {
          isPrBpmValid = false;
        }

        // Check if SpO2 value is empty or N/A
        const spO2Value = selectedValues[key].spo2;
        if (!spO2Value || spO2Value === "N/A" || spO2Value === "-") {
          isSpO2Valid = false;
        }
      }
    }

    setFormValidation((prev) => ({
      ...prev,
      dyspneaFatigueValid: isDyspneaFatigueValid,
      prBpmValid: isPrBpmValid,
      spO2Valid: isSpO2Valid,
    }));
  }, [dyspneaValues, fatigueValues, selectedValues]);

  const handleGenerate = async (values, { setSubmitting }) => {
    try {
      // Check if dyspnea and fatigue values are provided
      if (
        !dyspneaValues.rest ||
        !dyspneaValues.max ||
        !fatigueValues.rest ||
        !fatigueValues.max
      ) {
        setAlert({
          showAlert: true,
          severity: "error",
          message:
            "Dyspnea and Fatigue values are required for both Resting and Max.",
        });
        setSubmitting(false);
        return;
      }

      const { data } = await axios.patch(`/6mwt/create-report`, {
        report_id: formData?.report_id,
        test_date: values?.date,
        // reviewing_physician_id: values?.assignedTo,
        uom: values.units,
        patient_name: values.name === "" ? null : values.name,
        patient_id: values.patientId === "" ? null : values.patientId,
        height:
          values.units === "customary"
            ? 12 * parseInt(values?.height_ft) + parseInt(values?.height_in)
            : parseInt(values?.height_cm),
        weight: parseInt(values?.weight),
        dob: values?.birthday,
        birth_sex: values?.birthSex,
        gender: values?.gender === "" ? null : values?.gender,
        smoking_history:
          values?.smokingHistory === "" ? null : values?.smokingHistory,
        predicted_group:
          values?.predictedGroup === "" ? null : values?.predictedGroup,
        icd_10_code: values?.icd10 === "" ? null : values?.icd10,
        technician_name: values?.technician,
        attending_physician_name: values?.attendingPhysician,
        reviewing_physician_name: values?.referringPhysician,
        actual_6mw_distance: values?.actualDistance,
        supp_oxygen_during_test:
          values?.supplementalOxygen === "" ? null : values?.supplementalOxygen,
        predicted_distance: parseInt(values?.predictedDistance),
        lln: parseInt(values?.lln),
        percent_predicted: parseInt(values?.percentPredicted),
        avg_walk_speed: parseInt(values?.avgWalkingSpeed),
        walk_statement: values?.walkStatement,
        technologists_comment: values?.techComments,
        bmi: values?.bmi,
        age: values?.age,
        rest_min: selectedValues?.rest,
        walk_min_1: selectedValues?.walk_1,
        walk_min_2: selectedValues?.walk_2,
        walk_min_3: selectedValues?.walk_3,
        walk_min_4: selectedValues?.walk_4,
        walk_min_5: selectedValues?.walk_5,
        walk_min_6: selectedValues?.walk_6,
        recovery_min_1: selectedValues?.recovery_1,
        recovery_min_2: selectedValues?.recovery_2,
        recovery_min_3: selectedValues?.recovery_3,
        // Update to use the values from the state variables
        rest_min_dyspnea: parseInt(dyspneaValues.rest),
        walk_min_dyspnea: parseInt(dyspneaValues.max),
        rest_min_fatigue: parseInt(fatigueValues.rest),
        walk_min_fatigue: parseInt(fatigueValues.max),
        rest_min_bp: bpValues.rest || "",
        walk_min_bp: bpValues.max || "",
        recovery_min_1_bp: bpValues.recovery_1 || "",
        recovery_min_2_bp: bpValues.recovery_2 || "",
        recovery_min_3_bp: bpValues.recovery_3 || "",
      });

      if (data?.status === "OK") {
        // On successful submission, clear the saved form data
        dispatch(clearWalkTestFormData());

        setAlert({
          showAlert: true,
          severity: "success",
          message: "Report generated successfully",
        });
        handleConfirmExit();

        loadCompletedData();
        loadWaitingData();
        onWalkTestReportGenerated?.();
      } else {
        setAlert({
          showAlert: true,
          severity: "error",
          message: data?.message,
        });
      }
      // setFormData(data?.data);
      // setStep(2);
    } catch (error) {
      console.error("Submit failed:", error);
    } finally {
      setSubmitting(false);
    }
  };

  // Modify the handleBack function to save form data to Redux
  const handleBackWithState = () => {
    // Get current form values from formikRef
    const currentFormValues = formikRef.current?.values || {};

    // Save all form state to Redux
    dispatch(
      setWalkTestFormData({
        formData: currentFormValues,
        dyspneaValues,
        fatigueValues,
        bpValues,
      })
    );

    // Call the original onBack function
    onBack(currentFormValues);
  };

  return (
    <>
      <div className={styles.contentWrapper}>
        <Formik
          innerRef={formikRef}
          initialValues={{
            ...patientInitialValues,
            ...(savedFormData || {}), // Use saved data from Redux if available
            technician: formData?.technician ?? "",
            date: formData?.date ?? "",
          }}
          validationSchema={validationSchema}
          onSubmit={handleGenerate}
        >
          {({
            values,
            setFieldValue,
            errors,
            touched,
            isSubmitting,
            isValid,
          }) => (
            <Form>
              <Calculator />

              <div className={styles.radioGroup}>
                <RadioGroup
                  row
                  name="units"
                  value={values.units}
                  onChange={(e) =>
                    handleUnitChange(setFieldValue, e.target.value, values)
                  }
                >
                  <FormControlLabel
                    value="customary"
                    control={
                      <Radio
                        sx={{
                          color: "var(--primary-theme-color)",
                          "&.Mui-checked": {
                            color: "var(--primary-theme-color)",
                          },
                        }}
                      />
                    }
                    label={
                      <Typography
                        style={{
                          fontFamily: "Poppins",
                          fontSize: "14px",
                          color: "var(--primary-text-color)",
                        }}
                      >
                        US Customary Units
                      </Typography>
                    }
                    style={{ marginRight: "24px" }}
                  />
                  <FormControlLabel
                    value="metric"
                    control={
                      <Radio
                        sx={{
                          color: "var(--primary-theme-color)",
                          "&.Mui-checked": {
                            color: "var(--primary-theme-color)",
                          },
                        }}
                      />
                    }
                    label={
                      <Typography
                        style={{
                          fontFamily: "Poppins",
                          fontSize: "14px",
                          color: "var(--primary-text-color)",
                        }}
                      >
                        Metric Units
                      </Typography>
                    }
                  />
                </RadioGroup>
              </div>

              <div className={styles.divider} />

              {/* Patient Details */}
              <div style={{ marginBottom: "24px" }}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr",
                    gap: "16px",
                  }}
                >
                  <div>
                    <Typography
                      className={`${styles.label} ${styles.required}`}
                    >
                      Name
                    </Typography>
                    <Field name="name" className={styles.inputField} />
                    {errors.name && touched.name && (
                      <div className={styles.errorText}>{errors.name}</div>
                    )}
                  </div>
                  <div>
                    <Typography
                      className={`${styles.label} ${styles.required}`}
                    >
                      Date
                    </Typography>
                    <Field name="date">
                      {({ field }) => (
                        <DateTimeField type="date" disabled {...field} />
                      )}
                    </Field>
                  </div>
                  <div>
                    <Typography className={styles.label}>
                      Patient ID
                      <span style={{ color: "#666666", fontSize: "12px" }}>
                        {" "}
                        (Optional)
                      </span>
                    </Typography>
                    <Field
                      name="patientId"
                      className={`${styles.inputField} ${
                        errors.patientId && touched.patientId
                          ? styles.inputError
                          : ""
                      }`}
                    />
                    {errors.patientId && touched.patientId && (
                      <div className={styles.errorText}>{errors.patientId}</div>
                    )}
                  </div>
                </div>
              </div>

              <div className={styles.divider} />

              {/* Height & Weight */}
              <div className={styles.gridContainer}>
                <div>
                  <Typography className={`${styles.label} ${styles.required}`}>
                    Height
                  </Typography>
                  {values.units === "customary" ? (
                    <div className={styles.flexRow}>
                      <Field
                        name="height_ft"
                        placeholder="feet (ft)"
                        className={`${styles.inputField} ${
                          errors.height_ft && touched.height_ft
                            ? styles.inputError
                            : ""
                        }`}
                        type="number"
                      />
                      <Field
                        name="height_in"
                        placeholder="inches (in)"
                        className={`${styles.inputField} ${
                          errors.height_in && touched.height_in
                            ? styles.inputError
                            : ""
                        }`}
                        type="number"
                      />
                    </div>
                  ) : (
                    <Field
                      name="height_cm"
                      placeholder="centimeters (cm)"
                      className={`${styles.inputField} ${
                        errors.height_cm && touched.height_cm
                          ? styles.inputError
                          : ""
                      }`}
                      type="number"
                    />
                  )}
                  {((errors.height_ft && touched.height_ft) ||
                    (errors.height_in && touched.height_in) ||
                    (errors.height_cm && touched.height_cm)) && (
                    <div className={styles.errorText}>Height is required</div>
                  )}
                </div>
                <div>
                  <Typography className={`${styles.label} ${styles.required}`}>
                    Weight
                  </Typography>
                  <Field
                    name="weight"
                    placeholder={
                      values.units === "customary"
                        ? "pounds (lbs)"
                        : "kilograms (kg)"
                    }
                    className={`${styles.inputField} ${
                      errors.weight && touched.weight ? styles.inputError : ""
                    }`}
                    type="number"
                  />
                  {errors.weight && touched.weight && (
                    <div className={styles.errorText}>{errors.weight}</div>
                  )}
                </div>
                <div>
                  <Typography className={styles.label}>BMI</Typography>
                  <Field name="bmi" disabled className={styles.disabledInput} />
                  {values.bmi && (
                    <Typography className={styles.helperText}>
                      {`BMI: ${values.bmi} kg/m²`}
                    </Typography>
                  )}
                </div>
              </div>

              <div className={styles.divider} />

              {/* Birth Date & Age */}
              <div className={styles.gridContainer}>
                <div>
                  <Typography className={`${styles.label} ${styles.required}`}>
                    Birth Date
                  </Typography>
                  <Field name="birthday">
                    {({ field, form: { setFieldValue } }) => (
                      <DateTimeField
                        type="date"
                        {...field}
                        className={
                          errors.birthday && touched.birthday
                            ? styles.inputError
                            : ""
                        }
                        max={new Date().toISOString().split("T")[0]}
                        onChange={(e) => {
                          const value = e.target.value;
                          field.onChange(e);
                          setFieldValue("birthday", value);
                          setFieldValue("age", calculateAge(value));
                        }}
                      />
                    )}
                  </Field>
                  {errors.birthday && touched.birthday && (
                    <div className={styles.errorText}>{errors.birthday}</div>
                  )}
                </div>
                <div>
                  <Typography className={styles.label}>Age</Typography>
                  <Field name="age" disabled className={styles.disabledInput} />
                </div>
                <div></div> {/* Empty div to maintain grid layout */}
              </div>

              {/* Birth Sex and Gender line */}
              <div className={styles.gridContainer}>
                <div>
                  <Typography className={`${styles.label} ${styles.required}`}>
                    Birth Sex
                  </Typography>
                  <Field
                    name="birthSex"
                    as="select"
                    className={`${styles.inputField} ${
                      errors.birthSex && touched.birthSex
                        ? styles.inputError
                        : ""
                    }`}
                  >
                    <option value=""></option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </Field>
                  {errors.birthSex && touched.birthSex && (
                    <div className={styles.errorText}>{errors.birthSex}</div>
                  )}
                </div>
                <div>
                  <Typography className={styles.label}>
                    Gender
                    <span style={{ color: "#666666", fontSize: "12px" }}>
                      {" "}
                      (Optional)
                    </span>
                  </Typography>
                  <Field
                    name="gender"
                    className={`${styles.inputField} ${
                      errors.gender && touched.gender ? styles.inputError : ""
                    }`}
                  />
                  {errors.gender && touched.gender && (
                    <div className={styles.errorText}>{errors.gender}</div>
                  )}
                </div>
                <div></div>
              </div>

              {/* ICD-10, Smoking History, and Predicted Group line */}
              <div className={styles.gridContainer}>
                <div>
                  <Typography className={styles.label}>
                    Smoking history (pk-yrs)
                    <span style={{ color: "#666666", fontSize: "12px" }}>
                      {" "}
                      (Optional)
                    </span>
                  </Typography>
                  <Field
                    name="smokingHistory"
                    className={styles.inputField}
                    type={"number"}
                  />
                  {errors.smokingHistory && touched.smokingHistory && (
                    <div className={styles.errorText}>
                      {errors.smokingHistory}
                    </div>
                  )}
                </div>
                <div>
                  <Typography className={styles.label}>
                    Predicted Group
                    <span style={{ color: "#666666", fontSize: "12px" }}>
                      {" "}
                      (Optional)
                    </span>
                  </Typography>
                  <Field
                    name="predictedGroup"
                    className={`${styles.inputField} ${
                      errors.predictedGroup && touched.predictedGroup
                        ? styles.inputError
                        : ""
                    }`}
                  />
                  {errors.predictedGroup && touched.predictedGroup && (
                    <div className={styles.errorText}>
                      {errors.predictedGroup}
                    </div>
                  )}
                </div>
                <div>
                  <Typography className={styles.label}>
                    ICD-10
                    <span style={{ color: "#666666", fontSize: "12px" }}>
                      {" "}
                      (Optional)
                    </span>
                  </Typography>
                  <Field name="icd10" as="select" className={styles.inputField}>
                    <option value="">Select ICD-10</option>
                    {icdTens.map((icd10) => (
                      <option key={icd10.value} value={icd10.value}>
                        {icd10.label}
                      </option>
                    ))}
                  </Field>
                </div>
              </div>

              <div className={styles.divider} />

              {/* Medical Personnel Section */}
              <div className={styles.gridContainer}>
                <div>
                  <Typography className={`${styles.label} ${styles.required}`}>
                    Team Staff
                  </Typography>
                  <Field
                    name="technician"
                    className={`${styles.inputField} ${
                      errors.technician && touched.technician
                        ? styles.inputError
                        : ""
                    }`}
                    disabled
                  />
                  {errors.technician && touched.technician && (
                    <div className={styles.errorText}>{errors.technician}</div>
                  )}
                </div>
                <div>
                  <Typography className={`${styles.label} ${styles.required}`}>
                    Attending Team Staff
                  </Typography>
                  <Field
                    name="attendingPhysician"
                    className={`${styles.inputField} ${
                      errors.attendingPhysician && touched.attendingPhysician
                        ? styles.inputError
                        : ""
                    }`}
                  />
                  {errors.attendingPhysician && touched.attendingPhysician && (
                    <div className={styles.errorText}>
                      {errors.attendingPhysician}
                    </div>
                  )}
                </div>
                <div>
                  <Typography className={`${styles.label} ${styles.required}`}>
                    Referring Team Staff
                  </Typography>
                  <Field
                    name="referringPhysician"
                    className={`${styles.inputField} ${
                      errors.referringPhysician && touched.referringPhysician
                        ? styles.inputError
                        : ""
                    }`}
                  />
                  {errors.referringPhysician && touched.referringPhysician && (
                    <div className={styles.errorText}>
                      {errors.referringPhysician}
                    </div>
                  )}
                </div>
              </div>

              <div className={styles.divider} />

              {/* Walk Test Chart */}
              <WalkTestChart
                formData={formData}
                selectedValues={selectedValues}
                setSelectedValues={setSelectedValues}
                supplementalOxygen={values.supplementalOxygen}
                dyspneaValues={dyspneaValues}
                setDyspneaValues={setDyspneaValues}
                fatigueValues={fatigueValues}
                setFatigueValues={setFatigueValues}
                bpValues={bpValues}
                setBpValues={setBpValues}
              />

              <div className={styles.divider} />

              {/* Walk Test Measurements */}
              <div className={styles.gridContainer}>
                <div>
                  <Typography className={`${styles.label} ${styles.required}`}>
                    Actual 6MW distance
                  </Typography>
                  <div className={styles.flexRow}>
                    <Field
                      type="number"
                      name="actualDistance"
                      className={`${styles.inputField} ${
                        errors.actualDistance && touched.actualDistance
                          ? styles.inputError
                          : ""
                      }`}
                    />
                    <Typography
                      style={{ marginLeft: "8px", alignSelf: "center" }}
                    >
                      {values.units === "customary" ? "feet" : "meters"}
                    </Typography>
                  </div>
                  {errors.actualDistance && touched.actualDistance && (
                    <div className={styles.errorText}>
                      {errors.actualDistance}
                    </div>
                  )}
                </div>
                <div>
                  <Typography className={styles.label}>
                    Supplemental O2 (Optional)
                  </Typography>
                  <Field
                    name="supplementalOxygen"
                    className={`${styles.inputField} ${
                      errors.supplementalOxygen && touched.supplementalOxygen
                        ? styles.inputError
                        : ""
                    }`}
                  />
                  {errors.supplementalOxygen && touched.supplementalOxygen && (
                    <div className={styles.errorText}>
                      {errors.supplementalOxygen}
                    </div>
                  )}
                </div>
                <div></div>
              </div>

              <div className={styles.gridContainer}>
                <div>
                  <Typography className={styles.label}>
                    Predicted distance
                  </Typography>
                  <div className={styles.flexRow}>
                    <Field
                      name="predictedDistance"
                      className={styles.disabledInput}
                      disabled
                    />
                    <Typography
                      style={{ marginLeft: "8px", alignSelf: "center" }}
                    >
                      {values.units === "customary" ? "feet" : "meters"}
                    </Typography>
                  </div>
                </div>
                <div>
                  <Typography className={styles.label}>LLN</Typography>
                  <div className={styles.flexRow}>
                    <Field
                      name="lln"
                      className={styles.disabledInput}
                      disabled
                    />
                    <Typography
                      style={{ marginLeft: "8px", alignSelf: "center" }}
                    >
                      {values.units === "customary" ? "feet" : "meters"}
                    </Typography>
                  </div>
                </div>
                <div>
                  <Typography className={styles.label}>
                    Percent of predicted
                  </Typography>
                  <div className={styles.flexRow}>
                    <Field
                      name="percentPredicted"
                      className={styles.disabledInput}
                      disabled
                    />
                    <Typography
                      style={{ marginLeft: "8px", alignSelf: "center" }}
                    >
                      %
                    </Typography>
                  </div>
                </div>
              </div>

              <div className={styles.gridContainer}>
                <div>
                  <Typography className={styles.label}>
                    Avg. walking speed
                  </Typography>
                  <div className={styles.flexRow}>
                    <Field
                      name="avgWalkingSpeed"
                      className={styles.disabledInput}
                      disabled
                    />
                    <Typography
                      style={{ marginLeft: "8px", alignSelf: "center" }}
                    >
                      {values.units === "customary" ? "mph" : "km/h"}
                    </Typography>
                  </div>
                </div>
                <div></div>
                <div></div>
              </div>

              <div className={styles.divider} />

              {/* Comments Section */}
              <div className={styles.gridContainer}>
                <div style={{ gridColumn: "1 / -1" }}>
                  <Typography className={`${styles.label} ${styles.required}`}>
                    6-Minute Walk Statement
                  </Typography>
                  <Field
                    name="walkStatement"
                    as="textarea"
                    className={`${styles.inputField} ${styles.textArea} ${
                      errors.walkStatement && touched.walkStatement
                        ? styles.inputError
                        : ""
                    }`}
                    rows={4}
                  />
                  {errors.walkStatement && touched.walkStatement && (
                    <div className={styles.errorText}>
                      {errors.walkStatement}
                    </div>
                  )}
                </div>
              </div>

              <div className={styles.gridContainer}>
                <div style={{ gridColumn: "1 / -1" }}>
                  <Typography className={`${styles.label} ${styles.required}`}>
                    Team Staff Comments
                  </Typography>
                  <Field
                    name="techComments"
                    as="textarea"
                    className={`${styles.inputField} ${styles.textArea} ${
                      errors.techComments && touched.techComments
                        ? styles.inputError
                        : ""
                    }`}
                    rows={4}
                  />
                  {errors.techComments && touched.techComments && (
                    <div className={styles.errorText}>
                      {errors.techComments}
                    </div>
                  )}
                </div>
              </div>

              {/* Assign to person is not needed for the 6-Minute Walk Test report.
              <div className={styles.gridContainer}>
                <div style={{ gridColumn: "1 / -1" }}>
                  <Typography className={`${styles.label} ${styles.required}`}>
                    Assign to
                  </Typography>
                  <Field
                    name="assignedTo"
                    as="select"
                    className={`${styles.inputField} ${
                      errors.assignedTo && touched.assignedTo
                        ? styles.inputError
                        : ""
                    }`}
                  >
                    <option value="">Select Assign to</option>
                    {admins.map((admin) => (
                      <option key={admin.value} value={admin.value}>
                        {admin.label}
                      </option>
                    ))}
                  </Field>
                  {errors.assignedTo && touched.assignedTo && (
                    <div className={styles.errorText}>{errors.assignedTo}</div>
                  )}
                </div>
              </div>
              */}

              <div className={styles.divider} />

              {/* Buttons */}
              <div className={styles.buttonContainer}>
                <Button
                  type="button"
                  onClick={handleBackWithState}
                  style={{
                    fontSize: "14px",
                    fontFamily: "Poppins",
                    fontWeight: 500,
                    backgroundColor: "white",
                    color: "#0F9BB0",
                    textTransform: "none",
                    border: "2px solid",
                    borderColor: "#0F9BB0",
                    padding: "8px 24px",
                    minWidth: "120px",
                    borderRadius: "4px",
                  }}
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  disabled={
                    isSubmitting ||
                    !isValid ||
                    !formValidation.dyspneaFatigueValid ||
                    !formValidation.prBpmValid ||
                    !formValidation.spO2Valid
                  }
                  style={{
                    fontSize: "14px",
                    fontFamily: "Poppins",
                    fontWeight: 500,
                    backgroundColor: "#0F9BB0",
                    color: "white",
                    textTransform: "none",
                    padding: "8px 24px",
                    minWidth: "120px",
                    borderRadius: "4px",
                    marginLeft: "16px",
                    opacity:
                      isSubmitting ||
                      !isValid ||
                      !formValidation.dyspneaFatigueValid ||
                      !formValidation.prBpmValid ||
                      !formValidation.spO2Valid
                        ? "0.7"
                        : "1",
                  }}
                >
                  Generate
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>

      {/* <ExitConfirmDialog
        open={showExitConfirm}
        onClose={handleCancelExit}
        onConfirm={handleConfirmExit}
      /> */}
    </>
  );
}

export default WalkTestSecondStep;
